import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { createSupplier, updateSupplier, deleteSupplier } from '../services/supplierService';
import { queryKeys } from '../constants/queryKeys';

/**
 * useCreateSupplier — Create a new supplier
 *
 * Usage in AddSupplier.jsx:
 *   const createMutation = useCreateSupplier();
 *
 *   const onSubmit = (data) => {
 *     createMutation.mutate(data);
 *   };
 */
export const useCreateSupplier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => createSupplier(data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      toast.success('Supplier created successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to create supplier. Please try again.';
      toast.error(message);
    },
  });
};

/**
 * useUpdateSupplier — Update an existing supplier
 *
 * Usage:
 *   const updateMutation = useUpdateSupplier();
 *   updateMutation.mutate({ id: 5, data: formData });
 */
export const useUpdateSupplier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateSupplier(id, data),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      toast.success('Supplier updated successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to update supplier. Please try again.';
      toast.error(message);
    },
  });
};

/**
 * useDeleteSupplier — Delete a supplier
 *
 * Usage:
 *   const deleteMutation = useDeleteSupplier();
 *   deleteMutation.mutate(supplierId);
 */
export const useDeleteSupplier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteSupplier(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      toast.success('Supplier deleted successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to delete supplier. Please try again.';
      toast.error(message);
    },
  });
};
